import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";

export interface ComboBoxOption {
  value: string;
  label: string;
}

interface ComboBoxProps {
  options: ComboBoxOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

/**
 * A searchable combobox component that allows both selecting from a dropdown
 * and typing in a custom value. Shows options in "value - description" format.
 * Uses a portal for the dropdown to avoid overflow clipping issues.
 */
export function ComboBox({
  options,
  value,
  onChange,
  placeholder = "Select or type...",
  className = "",
  disabled = false,
}: ComboBoxProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Sync input value with external value changes
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Filter options based on input
  useEffect(() => {
    let filtered: ComboBoxOption[];
    if (!inputValue) {
      filtered = [...options];
    } else {
      const searchTerm = inputValue.toLowerCase();
      filtered = options.filter(
        (option) =>
          option.value.toLowerCase().includes(searchTerm) ||
          option.label.toLowerCase().includes(searchTerm),
      );
    }
    // Sort options by value (account number) in ascending order
    filtered.sort((a, b) => a.value.localeCompare(b.value, undefined, { numeric: true }));
    setFilteredOptions(filtered);
    setHighlightedIndex(-1);
  }, [inputValue, options]);

  // Calculate dropdown position when opening
  useEffect(() => {
    if (isOpen && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const dropdownHeight = 240; // Fixed max height for dropdown

      // Determine if dropdown should open upward or downward
      const openUpward = spaceBelow < 100 && spaceAbove > spaceBelow;

      // Make dropdown wider to show full account number + description
      const dropdownWidth = Math.max(rect.width, 420);

      setDropdownStyle({
        position: "fixed",
        left: rect.left,
        width: dropdownWidth,
        top: openUpward ? rect.top - dropdownHeight - 4 : rect.bottom + 4,
        maxHeight: dropdownHeight,
      });
    }
  }, [isOpen, filteredOptions.length]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isInsideContainer = containerRef.current?.contains(target);
      const isInsideDropdown = dropdownRef.current?.contains(target);

      if (!isInsideContainer && !isInsideDropdown) {
        setIsOpen(false);
        // When losing focus, if the input doesn't match a valid option, keep the typed value
        if (inputValue !== value) {
          onChange(inputValue);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [inputValue, value, onChange]);

  // Close dropdown on scroll of parent containers (but not the dropdown itself)
  useEffect(() => {
    if (!isOpen) return;

    const handleScroll = (e: Event) => {
      // Don't close if scrolling within the dropdown itself
      if (dropdownRef.current?.contains(e.target as Node)) {
        return;
      }
      setIsOpen(false);
    };

    window.addEventListener("scroll", handleScroll, true);

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [isOpen]);

  // Scroll highlighted option into view
  useEffect(() => {
    if (highlightedIndex >= 0 && dropdownRef.current) {
      const highlightedElement = dropdownRef.current.children[highlightedIndex] as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: "nearest" });
      }
    }
  }, [highlightedIndex]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setIsOpen(true);
  }, []);

  const handleInputFocus = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleOptionClick = useCallback(
    (option: ComboBoxOption) => {
      setInputValue(option.value);
      onChange(option.value);
      setIsOpen(false);
      inputRef.current?.focus();
    },
    [onChange],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isOpen) {
        if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter") {
          setIsOpen(true);
          e.preventDefault();
        }
        return;
      }

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setHighlightedIndex((prev) => (prev < filteredOptions.length - 1 ? prev + 1 : prev));
          break;
        case "ArrowUp":
          e.preventDefault();
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
          break;
        case "Enter":
          e.preventDefault();
          if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
            handleOptionClick(filteredOptions[highlightedIndex]);
          } else {
            // Accept the typed value
            onChange(inputValue);
            setIsOpen(false);
          }
          break;
        case "Escape":
          e.preventDefault();
          setIsOpen(false);
          break;
        case "Tab":
          // Accept the current value and allow normal tab behavior
          if (inputValue !== value) {
            onChange(inputValue);
          }
          setIsOpen(false);
          break;
      }
    },
    [isOpen, highlightedIndex, filteredOptions, handleOptionClick, inputValue, value, onChange],
  );

  // Dropdown content to be rendered in a portal
  const dropdownContent = isOpen && (filteredOptions.length > 0 || inputValue) && (
    <div
      ref={dropdownRef}
      style={dropdownStyle}
      className="overflow-auto bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded shadow-lg z-[9999]"
    >
      {filteredOptions.length > 0 ? (
        filteredOptions.map((option, index) => (
          <div
            key={option.value}
            onClick={() => handleOptionClick(option)}
            className={`px-2 py-1.5 text-sm cursor-pointer truncate ${
              index === highlightedIndex
                ? "bg-blue-500 text-white"
                : "text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700"
            } ${option.value === value ? "font-semibold" : ""}`}
            title={option.label}
          >
            {option.label}
          </div>
        ))
      ) : (
        <div className="px-2 py-2 text-sm text-gray-500 dark:text-gray-400">
          No matches found. Press Enter to use "{inputValue}".
        </div>
      )}
    </div>
  );

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full px-2 py-1 pr-7 border border-gray-300 dark:border-gray-600 rounded text-sm dark:bg-gray-700 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          autoComplete="off"
        />
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="absolute right-1 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          tabIndex={-1}
        >
          <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
        </button>
      </div>

      {/* Render dropdown in a portal to escape overflow constraints */}
      {createPortal(dropdownContent, document.body)}
    </div>
  );
}
